import { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../firebaseConfig";

// ✅ Photos (add these files)
import tuvarImg from "../assets/procurement/tuvar.jpg";
import mustardImg from "../assets/procurement/mustard.jpg";
import groundnutImg from "../assets/procurement/groundnut.jpg";
import wheatImg from "../assets/procurement/wheat.jpg";
import paddyImg from "../assets/procurement/paddy.jpg";

const cx = (...xs) => xs.filter(Boolean).join(" ");
const INR = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

const FALLBACK_COMMODITIES = [
  // ✅ MSP rate 8000 / Quintal as you said
  { name: "Tuvar (તુવેર)", slug: "tuvar", msp: 8000, unit: "Quintal", agency: "NAFED", isActive: true, img: tuvarImg, tag: "NAFED MSP" },
  { name: "Mustard (રાયડો)", slug: "mustard", msp: 0, unit: "Quintal", agency: "MSP", isActive: true, img: mustardImg, tag: "MSP" },
  { name: "Groundnut (મગફળી)", slug: "groundnut", msp: 0, unit: "Quintal", agency: "MSP", isActive: true, img: groundnutImg, tag: "MSP" },
  { name: "Wheat (ઘઉં)", slug: "wheat", msp: 0, unit: "Quintal", agency: "MSP", isActive: true, img: wheatImg, tag: "MSP" },
  { name: "Paddy (ધાન)", slug: "paddy", msp: 0, unit: "Quintal", agency: "MSP", isActive: true, img: paddyImg, tag: "MSP" },
];

function Badge({ children, tone = "gray" }) {
  const cls =
    tone === "green"
      ? "bg-green-100 text-green-800 border-green-200"
      : tone === "red"
      ? "bg-red-100 text-red-800 border-red-200"
      : tone === "yellow"
      ? "bg-yellow-100 text-yellow-800 border-yellow-200"
      : "bg-gray-100 text-gray-800 border-gray-200";

  return (
    <span className={cx("inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border", cls)}>
      {children}
    </span>
  );
}

export default function ProcurementPage() {
  const [commodities, setCommodities] = useState([]);
  const [selected, setSelected] = useState(null);
  const [centres, setCentres] = useState([]);
  const [search, setSearch] = useState("");

  // ✅ Fetch commodities (अगर Firestore empty हो तो fallback दिखेगा)
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "commodities"), (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setCommodities(list);
    });
    return () => unsub();
  }, []);

  // ✅ Fetch centres for selected commodity
  useEffect(() => {
    if (!selected?.slug) {
      setCentres([]);
      return;
    }
    const qy = query(
      collection(db, "procurement_centres"),
      where("commoditySlug", "==", selected.slug)
    );
    return onSnapshot(qy, (snap) => {
      setCentres(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
  }, [selected]);

  // ✅ Use Firestore commodities if present else fallback
  const commodityCards = useMemo(() => {
    const fs = (commodities || [])
      .filter((c) => c.isActive)
      .map((c) => {
        // link image by slug
        const img =
          c.slug === "tuvar" ? tuvarImg :
          c.slug === "mustard" ? mustardImg :
          c.slug === "groundnut" ? groundnutImg :
          c.slug === "wheat" ? wheatImg :
          c.slug === "paddy" ? paddyImg :
          tuvarImg;

        return { ...c, img };
      });

    return fs.length ? fs : FALLBACK_COMMODITIES;
  }, [commodities]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return commodityCards;
    return commodityCards.filter((c) =>
      [c.name, c.slug, c.agency].some((x) => String(x || "").toLowerCase().includes(s))
    );
  }, [commodityCards, search]);

  const totals = useMemo(() => {
    const totalFarmers = centres.reduce((a, c) => a + Number(c.farmersRegistered || 0), 0);
    const totalQty = centres.reduce((a, c) => a + Number(c.quantityQtl || 0), 0);
    return { totalFarmers, totalQty };
  }, [centres]);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 flex items-center gap-2">
              🌾 Procurement – MSP Operations
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              MSP commodity પસંદ કરો → Centres, Address, Contact, Live Status જુઓ
            </p>
          </div>

          {/* Search */}
          <div className="w-full sm:w-96">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search commodity (tuvar / mustard / groundnut...)"
              className="w-full rounded-lg border bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-green-200"
            />
          </div>
        </div>

        {/* Commodity Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-6">
          {filtered.map((c) => {
            const isSel = selected?.slug === c.slug || selected?.id === c.id;
            return (
              <div
                key={c.id || c.slug}
                className={cx(
                  "rounded-2xl overflow-hidden border bg-white shadow-sm hover:shadow-md transition",
                  isSel ? "border-green-600 ring-2 ring-green-100" : "border-slate-200"
                )}
              >
                <div className="h-40 bg-slate-100 relative">
                  <img src={c.img} alt={c.name} className="h-full w-full object-cover" />
                  <div className="absolute top-3 left-3 flex gap-2">
                    <Badge tone="green">{c.agency || "MSP"}</Badge>
                    <Badge tone="yellow">{c.unit || "Quintal"}</Badge>
                  </div>
                </div>

                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="text-lg font-extrabold text-slate-900">{c.name}</div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        Commodity Code: <span className="font-semibold">{c.slug}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-slate-500">MSP Rate</div>
                      <div className="text-lg font-extrabold text-green-700">
                        {c.msp ? `${INR(c.msp)}` : "—"}
                      </div>
                      <div className="text-xs text-slate-500">/{c.unit || "Quintal"}</div>
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <Badge tone={c.slug === "tuvar" ? "green" : "gray"}>
                      {c.slug === "tuvar" ? "NAFED Through" : "MSP Operations"}
                    </Badge>
                    {c.tag ? <Badge tone="red">{c.tag}</Badge> : null}
                  </div>

                  <div className="mt-4 flex gap-2">
                    <button
                      onClick={() => setSelected(c)}
                      className="flex-1 rounded-lg bg-green-700 hover:bg-green-800 text-white text-sm font-semibold py-2"
                    >
                      View Centres →
                    </button>
                    <button
                      onClick={() => setSelected(c)}
                      className="rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-sm font-semibold px-3 py-2"
                    >
                      Enter
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Selected Commodity Centres */}
        {selected && (
          <div className="mt-10">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900">
                  📍 {selected.name} – Centres & Live Status
                </h2>
                <p className="text-sm text-slate-600 mt-1">
                  Centres details: Address, Contact person, Map, Farmers registered, Quantity received
                </p>
              </div>

              <div className="flex gap-3">
                <div className="rounded-xl border bg-white px-4 py-2">
                  <div className="text-xs text-slate-500">Total Farmers</div>
                  <div className="text-lg font-extrabold text-slate-900">{totals.totalFarmers}</div>
                </div>
                <div className="rounded-xl border bg-white px-4 py-2">
                  <div className="text-xs text-slate-500">Total Quantity</div>
                  <div className="text-lg font-extrabold text-slate-900">
                    {totals.totalQty.toLocaleString("en-IN")} Qtl
                  </div>
                </div>
              </div>
            </div>

            {centres.length === 0 ? (
              <div className="mt-5 rounded-xl border bg-white p-5 text-slate-600">
                अभी इस commodity के centres Firestore में add नहीं हुए हैं।  
                Admin Panel से centres add/Update करोगे तो यहां auto दिखेंगे ✅
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-5 mt-5">
                {centres.map((c) => (
                  <div key={c.id} className="rounded-2xl border bg-white p-5 shadow-sm">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-lg font-extrabold text-green-800">{c.centreName}</div>
                        <div className="text-sm text-slate-600 mt-1">{c.address}</div>
                      </div>
                      <Badge tone={String(c.status).toLowerCase() === "active" ? "green" : "gray"}>
                        {String(c.status || "active").toUpperCase()}
                      </Badge>
                    </div>

                    <div className="grid grid-cols-2 gap-3 mt-4">
                      <div className="rounded-xl bg-slate-50 border p-3">
                        <div className="text-xs text-slate-500">Farmers Registered</div>
                        <div className="text-xl font-extrabold text-slate-900">
                          {Number(c.farmersRegistered || 0).toLocaleString("en-IN")}
                        </div>
                      </div>
                      <div className="rounded-xl bg-slate-50 border p-3">
                        <div className="text-xs text-slate-500">Quantity Received</div>
                        <div className="text-xl font-extrabold text-slate-900">
                          {Number(c.quantityQtl || 0).toLocaleString("en-IN")}{" "}
                          <span className="text-sm font-semibold text-slate-600">Qtl</span>
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 text-sm text-slate-700">
                      <div>
                        👤 <span className="font-semibold">{c.contactPerson || "—"}</span>
                      </div>
                      <div className="mt-1">
                        ☎️ <span className="font-semibold">{c.phone || "—"}</span>
                      </div>
                    </div>

                    <div className="mt-4 flex flex-wrap gap-2">
                      {c.mapLink ? (
                        <a
                          href={c.mapLink}
                          target="_blank"
                          rel="noreferrer"
                          className="px-4 py-2 rounded-lg bg-blue-700 hover:bg-blue-800 text-white text-sm font-semibold"
                        >
                          📍 View Map
                        </a>
                      ) : (
                        <span className="px-4 py-2 rounded-lg bg-slate-100 border text-slate-600 text-sm font-semibold">
                          Map link not added
                        </span>
                      )}
                      <button
                        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                        className="px-4 py-2 rounded-lg border bg-white hover:bg-slate-50 text-sm font-semibold"
                      >
                        ↑ Back to Commodities
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Footer note */}
        <div className="mt-10 text-xs text-slate-500">
          નોંધ: ખરીદી કેન્દ્રોની અંતિમ મંજૂરી સંબંધિત સત્તાવાર સંસ્થાઓના નિર્દેશ અનુસાર રહેશે.
        </div>
      </div>
    </div>
  );
}
